import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { INoticeBoardPosts } from 'src/app/models/notice-board';

interface ReportReason {
  value: string;
  label: string;
  requiresDescription: boolean;
}

interface DialogData {
  post: INoticeBoardPosts;
}

interface ReportFormData {
  reason: string;
  description?: string;
}
@Component({
  selector: 'app-report-post-dialog',
  templateUrl: './report-post-dialog.component.html',
  styleUrl: './report-post-dialog.component.scss',
})
export class ReportPostDialogComponent implements OnInit {
  reportForm = new FormGroup({
    reason: new FormControl('', [Validators.required]),
    description: new FormControl(''),
  });

  readonly reportReasons: ReportReason[] = [
    { value: 'spam', label: 'Spam or misleading', requiresDescription: false },
    {
      value: 'harassment',
      label: 'Harassment or bullying',
      requiresDescription: true,
    },
    { value: 'hate_speech', label: 'Hate speech', requiresDescription: true },
    {
      value: 'violence',
      label: 'Violence or dangerous content',
      requiresDescription: true,
    },
    {
      value: 'inappropriate',
      label: 'Inappropriate content',
      requiresDescription: false,
    },
    {
      value: 'copyright',
      label: 'Copyright infringement',
      requiresDescription: true,
    },
    {
      value: 'false_info',
      label: 'False information',
      requiresDescription: false,
    },
    { value: 'other', label: 'Other', requiresDescription: true },
  ];

  isSubmitting = false;

  constructor(
    public dialogRef: MatDialogRef<ReportPostDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  ngOnInit(): void {
    this.setupFormValidation();
  }

  private setupFormValidation(): void {
    this.reportForm.get('reason')?.valueChanges.subscribe((reason) => {
      this.updateDescriptionValidation(reason);
    });
  }

  private updateDescriptionValidation(reason: string): void {
    const descriptionControl = this.reportForm.get('description');
    const selectedReason = this.reportReasons.find((r) => r.value === reason);

    if (selectedReason?.requiresDescription) {
      descriptionControl?.setValidators([
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]);
    } else {
      descriptionControl?.setValidators([Validators.maxLength(500)]);
    }

    descriptionControl?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.reportForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    const formValue = this.reportForm.value;
    const reportData: ReportFormData = {
      reason: formValue.reason!,
      description: formValue.description || undefined,
    };

    // Simulate API call delay
    setTimeout(() => {
      this.dialogRef.close(reportData);
    }, 500);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get selectedReason(): ReportReason | undefined {
    const reasonValue = this.reportForm.get('reason')?.value;
    return this.reportReasons.find((r) => r.value === reasonValue);
  }

  get showDescriptionField(): boolean {
    return this.selectedReason?.requiresDescription || false;
  }

  get isFormValid(): boolean {
    return this.reportForm.valid;
  }
}
